import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Plane, ArrowLeft, HelpCircle } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { FAQ_SEO, buildBreadcrumbJsonLd } from '../data/seoContent';
import { DEFAULT_LANGUAGE } from '../lib/i18nRouting';
import PublicPageLayout from '../components/layout/PublicPageLayout';

const PublicFAQHeader = () => (
  <header className="bg-[#1b2236] text-white border-b border-slate-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="faq-header-logo">
          <img src="/logo-landscape.png" alt="Drone Maps" className="h-12 sm:h-14 w-auto" />
        </Link>
        <nav className="flex items-center gap-4">
          <Link to="/" className="text-slate-300 hover:text-white transition-colors flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <Link to="/login" className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg transition-colors">
            Connexion
          </Link>
        </nav>
      </div>
    </div>
  </header>
);

const FAQ_DATA = [
  {
    category: "Réglementation Drone",
    questions: [
      {
        question: "Quelle est la réglementation drone en vigueur en France ?",
        answer: "En France, la réglementation drone est régie par le règlement européen EU 2019/947 depuis le 1er janvier 2021. Ce règlement définit trois catégories d'opérations : Ouverte (Open), Spécifique (Specific) et Certifiée (Certified). La catégorie Ouverte est subdivisée en sous-catégories A1, A2 et A3 selon le poids du drone et les conditions de vol."
      },
      {
        question: "Qu'est-ce que la catégorie Open (Ouverte) ?",
        answer: "La catégorie Open permet de voler sans autorisation préalable si vous respectez certaines conditions : altitude maximale de 120m, maintien en vue directe (VLOS), pas de survol de rassemblements de personnes. Elle comprend 3 sous-catégories : A1 (drones <900g, survol de personnes possible), A2 (drones <4kg, distance 30m des personnes), A3 (drones <25kg, zones non peuplées uniquement)."
      },
      {
        question: "Qu'est-ce que la catégorie Specific (Spécifique) ?",
        answer: "La catégorie Specific s'applique lorsque les conditions de la catégorie Open ne peuvent pas être respectées. Elle nécessite une autorisation de la DGAC ou une déclaration selon des scénarios standards (STS-01 pour le vol en zone urbaine, STS-02 pour le vol BVLOS). Une analyse de risque SORA peut être requise pour les opérations complexes."
      },
      {
        question: "Qu'est-ce qu'une classe CE pour un drone ?",
        answer: "Les classes CE (C0 à C6) sont des marquages européens qui certifient que le drone respecte les exigences techniques du règlement EU 2019/945. C0 : <250g, C1 : <900g, C2 : <4kg avec mode basse vitesse, C3/C4 : <25kg, C5/C6 : pour la catégorie Specific. La classe détermine dans quelles sous-catégories Open vous pouvez voler."
      },
      {
        question: "Dois-je enregistrer mon drone en France ?",
        answer: "Oui, si votre drone pèse 800g ou plus, ou s'il est équipé d'une caméra (sauf jouets), vous devez vous enregistrer sur AlphaTango (portail de la DGAC) et apposer votre numéro d'exploitant UAS sur le drone. Une formation en ligne gratuite est également obligatoire."
      }
    ]
  },
  {
    category: "Zones de Vol",
    questions: [
      {
        question: "Quelles sont les zones interdites au vol de drone ?",
        answer: "Les zones interdites comprennent : les zones P (Prohibited) comme les centrales nucléaires, les zones R (Restricted) nécessitant une autorisation, les CTR et TMA autour des aéroports, les zones Natura 2000 et parcs nationaux (selon réglementation locale), les agglomérations en catégorie A3. Drone Maps affiche toutes ces zones sur sa carte interactive."
      },
      {
        question: "Puis-je voler en zone urbaine avec mon drone ?",
        answer: "Oui, sous conditions. En catégorie A1 avec un drone <900g classe C1, vous pouvez survoler des personnes isolées. En catégorie A2 avec un drone <4kg classe C2, vous devez maintenir 30m de distance (5m en mode lent). Pour les drones plus lourds, la catégorie Specific avec STS-01 est nécessaire, impliquant une déclaration préalable."
      },
      {
        question: "Comment savoir si ma zone de vol est autorisée ?",
        answer: "Drone Maps analyse automatiquement votre zone de vol en croisant les données officielles de la DGAC/IGN. Notre carte affiche les restrictions UAS, les zones aériennes (CTR, TMA), les zones environnementales protégées (Natura 2000, Parcs Nationaux) et les zones DJI. Un rapport détaillé vous indique les autorisations nécessaires."
      },
      {
        question: "Qu'est-ce qu'une zone CTR ?",
        answer: "Une CTR (Control Zone) est une zone de contrôle autour d'un aéroport où le trafic aérien est géré par un contrôleur. Le vol de drone y est généralement interdit ou soumis à autorisation du SNA (Service de Navigation Aérienne). Drone Maps identifie automatiquement les CTR et vous fournit les contacts pour demander une autorisation."
      }
    ]
  },
  {
    category: "Utilisation de Drone Maps",
    questions: [
      {
        question: "Comment fonctionne Drone Maps ?",
        answer: "Drone Maps vous permet de : 1) Enregistrer votre flotte de drones avec leurs caractéristiques, 2) Planifier vos missions en dessinant la zone de vol sur la carte, 3) Analyser automatiquement la conformité réglementaire, 4) Identifier les administrations à contacter, 5) Générer des rapports PDF professionnels pour vos clients ou archives."
      },
      {
        question: "Quelles données Drone Maps utilise-t-il ?",
        answer: "Drone Maps utilise les données officielles de la Géoplateforme nationale (DGAC/IGN) pour les restrictions UAS, l'API Carto de l'IGN pour les zones environnementales (Natura 2000, Parcs Nationaux, Réserves Naturelles), et des données géospatiales pour les zones aériennes françaises. Les données sont mises à jour régulièrement."
      },
      {
        question: "Le rapport Drone Maps remplace-t-il une autorisation officielle ?",
        answer: "Non, Drone Maps est un outil d'aide à la décision et à la préparation de mission. Il ne se substitue pas aux autorisations officielles de la DGAC, des préfectures ou des gestionnaires de zones protégées. Le rapport vous aide à identifier les démarches à effectuer et les contacts appropriés."
      },
      {
        question: "Quelle est la différence entre le plan gratuit et le plan Solo ?",
        answer: "Le plan gratuit permet de créer jusqu'à 3 missions avec toutes les fonctionnalités de base. Le plan Solo (19€/mois) offre des missions illimitées, des rapports PDF sans filigrane, et un accès prioritaire aux nouvelles fonctionnalités. Les partenaires bénéficient des avantages Solo gratuitement."
      }
    ]
  }
];

// Schema.org JSON-LD pour le SEO
const generateFAQSchema = () => {
  const allQuestions = FAQ_DATA.flatMap(cat => cat.questions);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allQuestions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };
};

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors text-left"
      aria-expanded={isOpen}
    >
      <span className="font-medium text-slate-900 pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-sky-500 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANGUAGE;
  const seo = FAQ_SEO[lang] || FAQ_SEO.fr;
  const faqSchema = generateFAQSchema();
  const breadcrumb = buildBreadcrumbJsonLd([
    { name: 'Drone Maps', url: 'https://drone-maps.eu' },
    { name: 'FAQ', url: 'https://drone-maps.eu/faq' },
  ]);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      <SeoMeta
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        rawTitle
        jsonLd={[faqSchema, breadcrumb]}
      />

      <PublicPageLayout publicHeader={<PublicFAQHeader />} title="FAQ">
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
          {/* Hero */}
          <section className="py-16 bg-[#1b2236] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-16 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-sky-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-[Barlow] mb-4">
              Questions Fréquentes
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Tout ce que vous devez savoir sur la réglementation drone en France et l'utilisation de Drone Maps.
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <main className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {FAQ_DATA.map((category, catIndex) => (
              <div key={catIndex} className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 font-[Barlow] mb-6 flex items-center gap-3">
                  <span className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center text-sky-600 text-sm font-mono">
                    {catIndex + 1}
                  </span>
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.questions.map((q, qIndex) => (
                    <FAQItem
                      key={qIndex}
                      question={q.question}
                      answer={q.answer}
                      isOpen={openItems[`${catIndex}-${qIndex}`]}
                      onClick={() => toggleItem(catIndex, qIndex)}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* CTA */}
            <div className="mt-16 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold font-[Barlow] mb-4">
                Prêt à simplifier vos vérifications réglementaires ?
              </h3>
              <p className="text-sky-100 mb-6">
                Créez votre compte gratuitement et planifiez votre première mission en quelques minutes.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-white text-sky-600 px-6 py-3 rounded-lg font-semibold hover:bg-sky-50 transition-colors"
              >
                Commencer gratuitement
              </Link>
            </div>
          </div>
        </main>

          {/* Footer */}
          <footer className="bg-[#1b2236] text-white py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-slate-400">
                © {new Date().getFullYear()} Drone Maps. Tous droits réservés.
              </p>
            </div>
          </footer>
        </div>
      </PublicPageLayout>
    </>
  );
}
